import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { OrganicShapes } from '../components/decor/OrganicShapes';
import { Reveal } from '../components/Reveal';

const LAST_UPDATED = 'July 21, 2026';

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f9fbfc] via-white to-[#F4F7F9] py-16 lg:py-20">
        <OrganicShapes variant="hero" />
        <Reveal className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl lg:text-6xl text-[#1f2937] mb-6 leading-tight">Privacy Policy</h1>
          <p className="text-lg text-[#4B5563]">Last updated: {LAST_UPDATED}</p>
        </Reveal>
      </section>

      {/* Body */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8 text-[#4B5563] leading-relaxed">
            <div className="space-y-4">
              <p>
                This Privacy Policy explains how PleromaHealth LLC (&ldquo;PleromaHealth,&rdquo;
                &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) handles information in
                connection with this website (the &ldquo;Site&rdquo;). We are committed to protecting
                your privacy and to being transparent about the limited information our Site collects.
              </p>
              <p>
                <strong className="text-[#1f2937]">In short: we do not collect, process, sell, or
                use personal information through this Site.</strong> We gather only aggregate,
                anonymized website statistics that help us understand how the Site is used and how to
                improve it.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">1. Information We Do Not Collect</h2>
              <p>
                This Site is an informational website. We do not require you to create an account,
                and we do not ask you to submit personal information in order to browse the Site. We
                do not knowingly collect names, mailing addresses, dates of birth, government
                identifiers, financial information, or any protected health information (PHI) through
                this Site.
              </p>
              <p>
                We do not use this Site to collect, store, or process the personal or health
                information of patients, members, or plan participants. Any protected health
                information handled through our products and services is governed by the separate
                agreements and Business Associate Agreements (BAAs) we enter into with our customers,
                not by this Site or this Policy.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">2. Website Statistics We Collect</h2>
              <p>
                Like most websites, our servers and analytics tools automatically record limited,
                non-identifying technical information for the purpose of measuring and improving Site
                performance. This information is used only in aggregate and is not used to identify
                you. It may include:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Pages viewed and links clicked, and the time and date of those visits;</li>
                <li>General geographic region (such as country or city) derived from IP address;</li>
                <li>Browser type, device type, operating system, and screen size;</li>
                <li>The referring website or search term that brought you to the Site;</li>
                <li>Aggregate traffic volume and performance metrics.</li>
              </ul>
              <p>
                We use this statistical information solely to understand overall Site usage, diagnose
                technical issues, and improve the content and experience we offer. We do not attempt
                to link this data to any individual.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">3. Cookies and Similar Technologies</h2>
              <p>
                The Site may use cookies or similar technologies to support basic functionality and to
                compile the aggregate website statistics described above. These are used only for
                analytics and site operation and not to build a profile of you or to serve targeted
                advertising. Most web browsers allow you to refuse or delete cookies through your
                browser settings; doing so should not prevent you from using the Site.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">4. If You Contact Us</h2>
              <p>
                If you choose to contact us directly&mdash;for example, by emailing us at{' '}
                <a href="mailto:info@pleromahealth.com" className="text-[#34AFFB] hover:underline">
                  info@pleromahealth.com
                </a>
                &mdash;we will receive the information you voluntarily provide, such as your name,
                email address, and the contents of your message. We use that information only to
                respond to your inquiry and do not use it for unrelated marketing. Please do not
                include sensitive personal information or any protected health information in your
                messages to us.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">5. How We Share Information</h2>
              <p>
                We do not sell, rent, or trade information about visitors to the Site. We may share
                aggregate, non-identifying website statistics with service providers who help us
                operate and analyze the Site, and we may disclose information if required to do so by
                law or to protect our legal rights.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">6. Third-Party Links</h2>
              <p>
                The Site may contain links to third-party websites and services that we do not
                control. This Policy does not apply to those sites, and we are not responsible for
                their privacy practices. We encourage you to review the privacy policies of any
                third-party sites you visit.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">7. Data Security</h2>
              <p>
                We maintain reasonable administrative, technical, and physical safeguards designed to
                protect the limited information associated with the Site. However, no method of
                transmission over the Internet or electronic storage is completely secure, and we
                cannot guarantee absolute security.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">8. Children&rsquo;s Privacy</h2>
              <p>
                The Site is intended for a professional, business audience and is not directed to
                children under the age of 13. We do not knowingly collect personal information from
                children.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">9. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. When we do, we will revise the
                &ldquo;Last updated&rdquo; date at the top of this page. Your continued use of the Site
                after any changes indicates your acceptance of the updated Policy.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">10. Contact Us</h2>
              <p>
                If you have questions about this Privacy Policy, please contact us at{' '}
                <a href="mailto:info@pleromahealth.com" className="text-[#34AFFB] hover:underline">
                  info@pleromahealth.com
                </a>
                .
              </p>
              <p>PleromaHealth LLC · Austin, TX and Dallas, TX</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
